import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { withServerProps } from '@web/library/with-server-props';
import { useTranslation } from 'react-i18next';
import {
  IndexBreedersMapScreen,
  IndexBreedersMapFilters,
  IndexBreedersMapScreenProps,
  indexBreedersMapTranslationPaths,
} from 'shared/screens/breeders/index-breeders-map';
import { Page } from '@web/components/page';
import { PageBuilder } from 'shared/library/page-builder';
import { Animal, Breed } from 'shared/@types';
import { apiRoute } from 'shared/library/api-route';
import { View } from 'react-native';
import { H1, H2 } from 'shared/components/title';
import { Image } from 'shared/components/image';
import { useRoute } from 'shared/library/routes/route';

import { Button } from 'shared/components/button';
import { getCanonicalBreedPageRedirect } from 'shared/library/canonical-breed-page';
import { parseBreedersMapFilter } from 'shared/state/breeders-filter';

interface IndexBreedBreedersScreenProps extends IndexBreedersMapScreenProps {
  lockedBreed: Breed;
}

export default function IndexBreedBreeders(props: IndexBreedBreedersScreenProps) {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const title = t('breeders.index_breeders_map.locked_breed_title', {
    breed: props.lockedBreed!.name,
  });
  const description = t('breeders.index_breeders_map.locked_breed_description', {
    breed: props.lockedBreed!.name,
  });
  const listingsTitle =
    (props.lockedBreed.active_pets_count ?? 0) > 0
      ? t(
          props.lockedBreed.animal === Animal.Dog
            ? 'breeds.show_breed.available_puppies'
            : 'breeds.show_breed.available_kittens',
          { count: props.lockedBreed.active_pets_count },
        )
      : t('listings.index_listings.locked_breed_title', { breed: props.lockedBreed.name });

  return (
    <Page {...props} register={(seo) => seo.setTitle(title).setDescription(description)}>
      <IndexBreedersMapScreen {...props} lockedBreed={props.lockedBreed} />

      <View className={'border-brand-brown-dark/15 border-t bg-white p-3 md:p-6 md:py-12'}>
        <View className={'mx-auto w-full max-w-5xl'}>
          <View className={'gap-6 md:flex-row md:items-start md:gap-10'}>
            <View className={'md:w-1/3 md:min-w-72'}>
              <Image
                src={props.lockedBreed.avatar}
                containerClassName={'aspect-4/3 w-full'}
                imageClassName={'rounded object-cover'}
                sizeType={'card'}
              />
            </View>

            <View className={'md:flex-1 md:pt-1'}>
              <H1
                title={title}
                subtitle={props.lockedBreed.summary || description}
                marginBottom={false}
              />

              <View className={'mt-6 flex-row flex-wrap items-center gap-x-6 gap-y-1.5'}>
                <Button
                  href={route('breeds.show.listings', { breed: props.lockedBreed.slug })}
                  text={listingsTitle}
                  color={'brown'}
                  icon={falAngleRight}
                />

                <Button
                  href={route('breeds.show', { breed: props.lockedBreed.slug })}
                  text={t('listings.index_listings.buying_locked_breed_cta', {
                    breed: props.lockedBreed.name,
                  })}
                  color={'brown'}
                  icon={falAngleRight}
                />
              </View>
            </View>
          </View>

          <View className={'mt-9 max-w-prose'}>
            {props.lockedBreed.breed_questions.map((question) => (
              <H2 key={question.id} title={question.question} subtitle={question.answer} />
            ))}
          </View>
        </View>
      </View>
    </Page>
  );
}

const pageBuilder = new PageBuilder<
  IndexBreedBreedersScreenProps,
  IndexBreedersMapFilters & { breed: string }
>()
  .translationPaths([
    ...indexBreedersMapTranslationPaths,
    'screens.breeds.show_breed',
    'screens.listings.index_listings',
  ])
  .request(async ({ params }) => ({
    initialFilters: parseBreedersMapFilter(params),
  }))
  .request(({ req, params }) =>
    req<{ breed: Breed }>(apiRoute('breeds.show', { breed: params.breed })).then((data) => ({
      lockedBreed: data.breed,
    })),
  )
  .transformProps(({ props, params, ctx }) => {
    const redirect = getCanonicalBreedPageRedirect({
      breed: props.lockedBreed,
      requestedSlug: params.breed,
      locale: ctx.locale,
      hasWebResponse: Boolean(ctx.res),
      page: { name: 'breeds.show.breeders' },
    });

    if (redirect) {
      return redirect;
    }

    return {
      ...props,
      initialFilters: { ...props.initialFilters, breeds: [props.lockedBreed.id] },
    };
  })
  .deriveRouteParams((props) => ({
    breed: props.lockedBreed.slug,
  }));

export const getServerSideProps = withServerProps(pageBuilder);
